import { useState, useCallback, useMemo } from "react";
import type { SolaContext, SolaVisibilityType } from "@/lib/sola/types";

interface UseSolaContextProps {
  initialContext?: SolaContext;
  initialVisibility?: SolaVisibilityType;
}

export function useSolaContext({
  initialContext = {},
  initialVisibility = "private",
}: UseSolaContextProps = {}) {
  const [context, setContext] = useState<SolaContext>(initialContext);
  const [visibilityType, setVisibilityType] = useState<SolaVisibilityType>(initialVisibility);

  const updateContext = useCallback((updates: Partial<SolaContext>) => {
    console.log('[useSolaContext] Updating context with:', updates);
    setContext((prev) => {
      const newContext = { ...prev, ...updates };
      console.log('[useSolaContext] New context:', newContext);
      return newContext;
    });
  }, []);

  const updateCurrentProject = useCallback((currentProject: string) => {
    updateContext({ currentProject });
  }, [updateContext]);

  const updateSearchResults = useCallback((searchResults: any[]) => {
    updateContext({ searchResults });
  }, [updateContext]);

  const updateUsage = useCallback((usage: SolaContext["usage"]) => {
    updateContext({ usage });
  }, [updateContext]);

  const updateCurrentCandidate = useCallback((candidate: any) => {
    console.log('[useSolaContext] Updating current candidate:', candidate?.id || candidate?.uuid);
    updateContext({ currentCandidate: candidate });
  }, [updateContext]);

  const clearContext = useCallback(() => {
    setContext({});
  }, []);

  const contextSummary = useMemo(() => {
    const summary: string[] = [];

    if (context.currentCandidate) {
      summary.push(`Viewing: ${context.currentCandidate.name || 'Candidate'}`);
    }

    if (context.currentProject) {
      summary.push(`Project: ${context.currentProject}`);
    }

    if (context.searchResults?.length) {
      summary.push(`${context.searchResults.length} search results`);
    }

    return summary.length > 0 ? summary.join(" • ") : "No context";
  }, [context]);

  return {
    // State
    context,
    visibilityType,
    contextSummary,

    // Actions
    setContext,
    updateContext,
    updateCurrentProject,
    updateSearchResults,
    updateUsage,
    updateCurrentCandidate,
    clearContext,
    setVisibilityType,
  };
}